// mission-cost-accounting.ts — P13 Agent B (chi phí + hạn mức).
//
// Accumulates token usage/cost per mission from journaled
// `attempt_usage_recorded` events. Deliberately does NOT go through
// mission-state-reducer.ts/MissionState — that file's `attempt_finished`
// switch case (and the whole MissionState shape) is mission-state-reducer.ts,
// which phase-13's ownership map reserves for Agent A ("chỉ field
// schema_error_detail trong ticket_last_verdict"). Reading the journal
// directly (scanJournal, same read-only pattern supervisor-dispatch-
// guards.ts's readOpenAttemptIds/readAttemptTimestampsIso already use) keeps
// this module's accumulation logic entirely inside Agent B's own file while
// still persisting durably via MissionStore.commit (fsync'd journal, the
// real source of truth) — see evidence doc for the ownership-conflict note.

// P13-review split (must-fix #2 — this file was 205 lines, over the
// 200-line/file budget) AND must-fix #3 (perf: computeMissionCost no longer
// re-scans the whole journal every call — see that file's top-of-file note
// for the caching design). Imported-then-re-exported here so every existing
// `from "./mission-cost-accounting.js"` import keeps compiling unchanged.
import {
  ATTEMPT_USAGE_RECORDED_KIND,
  type MissionCostSummary,
} from "./mission-cost-accounting-types.js";
import { computeMissionCost } from "./mission-cost-journal-reader.js";
import type { MissionCommitInput, MissionStore } from "./mission-store.js";
import { usdCeilingOrUndefined } from "./schemas/config-budget-ceilings.js";

export { ATTEMPT_USAGE_RECORDED_KIND, computeMissionCost, type MissionCostSummary };

export interface RecordAttemptUsageInput {
  ticket: string;
  attemptNo: number;
  /** Undefined when the worker's result event carried no `usage` object at all. */
  inputTokens: number | undefined;
  outputTokens: number | undefined;
  /** Undefined when the provider never reports a cost (never fabricated here). */
  costUsd: number | undefined;
}

export interface CostAttemptLimitsConfig {
  /** 3.38.1: optional, no default — absent ⇒ no USD ceiling. The schema refuses 0; a 0 here can only come from a pre-R5 effective-config snapshot and still reads as "no ceiling". */
  max_cost_usd_per_mission?: number;
  max_attempts_per_mission: number;
}

export type CostLimitBlockCode = "cost_limit_reached" | "attempt_limit_reached";

export interface CostLimitDecision {
  blocked: boolean;
  code?: CostLimitBlockCode;
  reason?: string;
}

/**
 * Append one `attempt_usage_recorded` event to the mission journal (fsync'd
 * by MissionStore.commit — same durability guarantee as every other mission
 * delta). Never throws on missing usage fields: a worker that crashed before
 * emitting a result event still gets an attempt counted, just with zero
 * tokens and no cost figure (test: "missing usage does not crash").
 */
export async function recordAttemptUsage(
  store: MissionStore,
  input: RecordAttemptUsageInput,
): Promise<void> {
  // `MissionDelta.kind: MissionDeltaKind` is a closed union owned by
  // mission-state-reducer.ts (phase-13 ownership: Agent A only, "chỉ field
  // schema_error_detail" — this module deliberately does not touch that
  // file, see the top-of-file note). `attempt_usage_recorded` is a real,
  // intentional new kind the reducer's `default: break` simply ignores
  // (event still fsync's into journal.jsonl — computeMissionCost below
  // reads it straight from there). The cast is narrow and local: the object
  // already satisfies MissionDelta's actual runtime shape
  // (Record<string, unknown> + a string `kind`), only the nominal union
  // rejects a kind literal the reducer hasn't been told about yet.
  const commitInput: MissionCommitInput = {
    event_id: `attempt-usage-${input.ticket}-${input.attemptNo}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    delta: {
      kind: ATTEMPT_USAGE_RECORDED_KIND,
      ticket: input.ticket,
      attemptNo: input.attemptNo,
      inputTokens: input.inputTokens,
      outputTokens: input.outputTokens,
      costUsd: input.costUsd,
    } as unknown as MissionCommitInput["delta"],
  };
  await store.commit([commitInput]);
}

/**
 * Pure comparison — NO literal thresholds baked in here (AST-gate covers
 * this file, section18-threshold-ast-gate.test.ts's GUARDED_FILES list):
 * both bounds come from `config`, and 0 means "unlimited" per the §18-style
 * convention already used by max_sessions_per_hour/min_free_disk_mb.
 */
export function evaluateCostAttemptLimits(
  cost: MissionCostSummary,
  config: CostAttemptLimitsConfig,
): CostLimitDecision {
  // Not `?? 0` — same AST-gate reason as computeMissionCost above: this
  // spendSoFar is derived data, not a §18 threshold fallback.
  const spendSoFar = cost.total_cost_usd === undefined ? 0 : cost.total_cost_usd;
  const usdCeiling = usdCeilingOrUndefined(config.max_cost_usd_per_mission);
  if (usdCeiling !== undefined && spendSoFar > usdCeiling) {
    return {
      blocked: true,
      code: "cost_limit_reached",
      reason: `mission cost $${spendSoFar.toFixed(4)} vượt trần max_cost_usd_per_mission ($${usdCeiling}).`,
    };
  }
  // H11 (audit 2026-09-28): `>=`, not `>` — this runs BEFORE a dispatch, so
  // at the cap the NEXT attempt must not start (cap 1 used to run two). Cost
  // keeps `>`: spend is a soft ceiling, the next session's price is unknown.
  if (
    config.max_attempts_per_mission > 0 &&
    cost.attempts_total >= config.max_attempts_per_mission
  ) {
    return {
      blocked: true,
      code: "attempt_limit_reached",
      reason: `mission đã chạy ${cost.attempts_total} attempt, chạm trần max_attempts_per_mission (${config.max_attempts_per_mission}).`,
    };
  }
  return { blocked: false };
}

/**
 * Combines computeMissionCost + evaluateCostAttemptLimits and, when
 * `store` is supplied and the ceiling IS breached, commits a
 * `mission_status_changed` -> WAITING_HUMAN journal receipt — kept here
 * (rather than inline in supervisor-dispatch-guards.ts) purely to keep that
 * file under the project's 200-line budget; this is the one call the
 * beforeDispatch gate needs for its last (permanent-once-breached) guard.
 */
export async function checkCostAttemptCeilingAndMaybeEscalate(
  controlRoot: string,
  config: CostAttemptLimitsConfig,
  store: MissionStore | undefined,
  now: Date,
): Promise<CostLimitDecision> {
  const cost = await computeMissionCost(controlRoot);
  const decision = evaluateCostAttemptLimits(cost, config);
  if (decision.blocked && decision.code && store) {
    await store.commit([
      {
        event_id: `dispatch-blocked-${decision.code}-${now.getTime()}`,
        delta: { kind: "mission_status_changed", status: "WAITING_HUMAN" },
      },
    ]);
  }
  return decision;
}
